import { applyTheme, type Density, type ThemeId } from './themes';
import {
  densityOf,
  prefsStore,
  themeChoiceOf,
  themeOf,
  usePrefsValue,
  type PrefsData,
} from '../prefs/prefsStore';

/** Appearance preferences (part of the synced preferences object). */
export function usePrefs() {
  const theme = usePrefsValue((s) => themeOf(s.data));
  const themeChoice = usePrefsValue((s) => themeChoiceOf(s.data));
  const density = usePrefsValue((s) => densityOf(s.data));
  return {
    /** The theme shown now. */
    theme,
    /** What the user picked: a theme, or 'system'. */
    themeChoice,
    density,
    setTheme: (value: ThemeId | 'system') => prefsStore.set('theme', value),
    setDensity: (value: Density) => prefsStore.set('density', value),
  };
}

/** Applies the saved theme now and after every change (also when it arrives from the server). Call once at start-up. */
export function startThemeSync(): () => void {
  const apply = (data: PrefsData) => applyTheme(themeOf(data), densityOf(data));
  apply(prefsStore.getState().data);
  const unsubscribe = prefsStore.subscribe((state, previous) => {
    if (state.data !== previous.data) apply(state.data);
  });
  // "Follow system": switch when the device goes light or dark.
  const media = typeof matchMedia === 'function' ? matchMedia('(prefers-color-scheme: light)') : null;
  const onSystemChange = () => {
    if (themeChoiceOf(prefsStore.getState().data) === 'system') apply(prefsStore.getState().data);
  };
  media?.addEventListener('change', onSystemChange);
  return () => {
    unsubscribe();
    media?.removeEventListener('change', onSystemChange);
  };
}
